/**
 * Curated preset cities for the QMD Generator demo.
 *
 * Each entry ships a rough bounding box (bbox) used by the sample-data
 * generator to keep every feature inside the city's actual land area.
 * This is the fix for "hospital in the Potomac" — even before road
 * snapping, points are clamped to city bounds so coastline cities don't
 * spawn hospitals in adjacent water.
 *
 * Bboxes are conservative bounding rectangles derived from OSM/Census
 * boundaries. They don't need to be pixel-perfect — the goal is to keep
 * generation inside a "mostly land" envelope. Road snapping still runs
 * afterwards for the final position.
 *
 * State accent color feeds into MapView's per-state symbology tint so the
 * demo feels regionally distinct.
 *
 * Adding a new city: (a) pick a lat/lon inside the built-up area,
 * (b) `radiusMiles` sized to the map type default (usually 2-5 mi),
 * (c) `bbox` = [minLat, minLon, maxLat, maxLon] covering the city polygon.
 */

export const STATE_META = {
  VA: { label: 'Virginia', accent: '#0B1F3A' },
  MD: { label: 'Maryland', accent: '#C72B1A' },
  SC: { label: 'South Carolina', accent: '#003D7C' },
  NC: { label: 'North Carolina', accent: '#8B0000' },
  FL: { label: 'Florida', accent: '#2A6AB8' },
  CA: { label: 'California', accent: '#D4A437' },
};

/** ordered list of state codes for tab UI */
export const STATE_ORDER = ['VA', 'MD', 'SC', 'NC', 'FL', 'CA'];

export const CITIES = [
  // ---- Virginia ----
  { state: 'VA', slug: 'alexandria',       name: 'Alexandria',       lat: 38.8048, lon: -77.0469, radiusMiles: 2, bbox: [38.784, -77.144, 38.850, -77.037] },
  { state: 'VA', slug: 'arlington',        name: 'Arlington',        lat: 38.8816, lon: -77.0910, radiusMiles: 3, bbox: [38.828, -77.170, 38.934, -77.032] },
  { state: 'VA', slug: 'richmond',         name: 'Richmond',         lat: 37.5407, lon: -77.4360, radiusMiles: 3, bbox: [37.446, -77.598, 37.612, -77.394] },
  { state: 'VA', slug: 'virginia_beach',   name: 'Virginia Beach',   lat: 36.8529, lon: -75.9780, radiusMiles: 4, bbox: [36.660, -76.240, 36.964, -75.870] },
  { state: 'VA', slug: 'martinsville',     name: 'Martinsville',     lat: 36.6916, lon: -79.8725, radiusMiles: 2, bbox: [36.657, -79.918, 36.732, -79.828] },
  { state: 'VA', slug: 'lynchburg',        name: 'Lynchburg',        lat: 37.4138, lon: -79.1422, radiusMiles: 3, bbox: [37.320, -79.240, 37.487, -79.043] },
  { state: 'VA', slug: 'roanoke',          name: 'Roanoke',          lat: 37.2710, lon: -79.9414, radiusMiles: 3, bbox: [37.212, -80.035, 37.360, -79.860] },

  // ---- Maryland ----
  { state: 'MD', slug: 'baltimore',        name: 'Baltimore',        lat: 39.2904, lon: -76.6122, radiusMiles: 3, bbox: [39.197, -76.712, 39.372, -76.529] },
  { state: 'MD', slug: 'annapolis',        name: 'Annapolis',        lat: 38.9784, lon: -76.4922, radiusMiles: 2, bbox: [38.930, -76.575, 39.020, -76.463] },
  { state: 'MD', slug: 'silver_spring',    name: 'Silver Spring',    lat: 38.9907, lon: -77.0261, radiusMiles: 2, bbox: [38.960, -77.075, 39.040, -76.980] },
  { state: 'MD', slug: 'rockville',        name: 'Rockville',        lat: 39.0840, lon: -77.1528, radiusMiles: 2, bbox: [39.040, -77.220, 39.135, -77.087] },
  { state: 'MD', slug: 'frederick',        name: 'Frederick',        lat: 39.4143, lon: -77.4105, radiusMiles: 2, bbox: [39.360, -77.470, 39.472, -77.352] },

  // ---- South Carolina ----
  { state: 'SC', slug: 'columbia',         name: 'Columbia',         lat: 34.0007, lon: -81.0348, radiusMiles: 3, bbox: [33.919, -81.135, 34.109, -80.930] },
  { state: 'SC', slug: 'charleston',       name: 'Charleston',       lat: 32.7833, lon: -79.9330, radiusMiles: 3, bbox: [32.706, -80.030, 32.900, -79.833] },
  { state: 'SC', slug: 'greenville',       name: 'Greenville',       lat: 34.8526, lon: -82.3940, radiusMiles: 3, bbox: [34.784, -82.478, 34.918, -82.310] },
  { state: 'SC', slug: 'myrtle_beach',     name: 'Myrtle Beach',     lat: 33.6891, lon: -78.8867, radiusMiles: 3, bbox: [33.596, -78.960, 33.789, -78.808] },
  { state: 'SC', slug: 'spartanburg',      name: 'Spartanburg',      lat: 34.9496, lon: -81.9320, radiusMiles: 2, bbox: [34.892, -82.000, 35.006, -81.850] },

  // ---- North Carolina ----
  { state: 'NC', slug: 'charlotte',        name: 'Charlotte',        lat: 35.2271, lon: -80.8431, radiusMiles: 4, bbox: [35.061, -81.018, 35.393, -80.669] },
  { state: 'NC', slug: 'raleigh',          name: 'Raleigh',          lat: 35.7796, lon: -78.6382, radiusMiles: 3, bbox: [35.700, -78.760, 35.900, -78.518] },
  { state: 'NC', slug: 'durham',           name: 'Durham',           lat: 35.9940, lon: -78.8986, radiusMiles: 3, bbox: [35.900, -79.010, 36.090, -78.790] },
  { state: 'NC', slug: 'greensboro',       name: 'Greensboro',       lat: 36.0726, lon: -79.7920, radiusMiles: 3, bbox: [35.987, -79.920, 36.170, -79.660] },
  { state: 'NC', slug: 'asheville',        name: 'Asheville',        lat: 35.5951, lon: -82.5515, radiusMiles: 2, bbox: [35.530, -82.630, 35.660, -82.470] },

  // ---- Florida ----
  { state: 'FL', slug: 'miami',            name: 'Miami',            lat: 25.7617, lon: -80.1918, radiusMiles: 3, bbox: [25.700, -80.320, 25.855, -80.145] },
  { state: 'FL', slug: 'orlando',          name: 'Orlando',          lat: 28.5383, lon: -81.3792, radiusMiles: 3, bbox: [28.450, -81.470, 28.634, -81.290] },
  { state: 'FL', slug: 'tampa',            name: 'Tampa',            lat: 27.9506, lon: -82.4572, radiusMiles: 3, bbox: [27.860, -82.554, 28.042, -82.360] },
  { state: 'FL', slug: 'jacksonville',     name: 'Jacksonville',     lat: 30.3322, lon: -81.6557, radiusMiles: 4, bbox: [30.195, -81.780, 30.470, -81.520] },
  { state: 'FL', slug: 'st_petersburg',    name: 'St. Petersburg',   lat: 27.7676, lon: -82.6403, radiusMiles: 3, bbox: [27.700, -82.760, 27.860, -82.600] },
  { state: 'FL', slug: 'tallahassee',      name: 'Tallahassee',      lat: 30.4383, lon: -84.2807, radiusMiles: 2, bbox: [30.380, -84.360, 30.500, -84.200] },

  // ---- California ----
  { state: 'CA', slug: 'los_angeles',      name: 'Los Angeles',      lat: 34.0522, lon: -118.2437, radiusMiles: 4, bbox: [33.900, -118.400, 34.200, -118.050] },
  { state: 'CA', slug: 'san_francisco',    name: 'San Francisco',    lat: 37.7749, lon: -122.4194, radiusMiles: 3, bbox: [37.700, -122.510, 37.830, -122.360] },
  { state: 'CA', slug: 'san_diego',        name: 'San Diego',        lat: 32.7157, lon: -117.1611, radiusMiles: 3, bbox: [32.615, -117.280, 32.815, -117.050] },
  { state: 'CA', slug: 'sacramento',       name: 'Sacramento',       lat: 38.5816, lon: -121.4944, radiusMiles: 3, bbox: [38.500, -121.580, 38.660, -121.400] },
  { state: 'CA', slug: 'san_jose',         name: 'San Jose',         lat: 37.3382, lon: -121.8863, radiusMiles: 3, bbox: [37.230, -121.990, 37.450, -121.780] },
  { state: 'CA', slug: 'oakland',          name: 'Oakland',          lat: 37.8044, lon: -122.2712, radiusMiles: 3, bbox: [37.720, -122.360, 37.890, -122.180] },
  { state: 'CA', slug: 'long_beach',       name: 'Long Beach',       lat: 33.7701, lon: -118.1937, radiusMiles: 3, bbox: [33.700, -118.280, 33.850, -118.090] },
];

export const CITIES_BY_STATE = STATE_ORDER.reduce((acc, s) => {
  acc[s] = CITIES.filter((c) => c.state === s);
  return acc;
}, {});

export function findCityBySlug(slug) {
  return CITIES.find((c) => c.slug === slug) || null;
}

/**
 * Clamp a (lon, lat) point into a bbox [minLat, minLon, maxLat, maxLon].
 * Points outside are pulled to the nearest edge so no feature spawns
 * outside the city envelope.
 */
export function clampToBbox(lon, lat, bbox) {
  if (!bbox || bbox.length !== 4) return [lon, lat];
  const [minLat, minLon, maxLat, maxLon] = bbox;
  return [
    Math.max(minLon, Math.min(maxLon, lon)),
    Math.max(minLat, Math.min(maxLat, lat)),
  ];
}

/**
 * Check whether a (lon, lat) point is inside a bbox.
 */
export function inBbox(lon, lat, bbox) {
  if (!bbox || bbox.length !== 4) return true;
  const [minLat, minLon, maxLat, maxLon] = bbox;
  return lon >= minLon && lon <= maxLon && lat >= minLat && lat <= maxLat;
}
